/**
 * Spreadsheet → array of header-keyed rows.
 *
 * Both parsers lower-case and trim the header row and coerce every cell to
 * a trimmed string, so callers downstream see one shape regardless of
 * whether the user uploaded CSV or XLSX.
 *
 * PapaParse (~13 KB) and ExcelJS (~700 KB) are both dynamic-imported so a
 * list page stays lean for the users who never import.
 */

type PapaParseModule = typeof import("papaparse");

/** CSV → array of header-keyed rows. */
export function parseCsv(file: File): Promise<Array<Record<string, string>>> {
  return new Promise((resolve, reject) => {
    void (async () => {
      const Papa: PapaParseModule = await import("papaparse");
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        complete: (r) => resolve(r.data),
        error: (e) => reject(new Error(e.message)),
      });
    })();
  });
}

/**
 * XLSX → array of header-keyed rows. Reads only the first worksheet and
 * treats row 1 as the header. Excel date cells become ISO `YYYY-MM-DD` and
 * formula cells become their computed result, matching the CSV shape.
 */
export async function parseXlsx(file: File): Promise<Array<Record<string, string>>> {
  const ExcelJS = (await import("exceljs")).default;
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  if (!ws) throw new Error("Workbook has no sheets.");

  const headers: string[] = [];
  const headerRow = ws.getRow(1);
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    headers[colNumber - 1] = String(cell.value ?? "")
      .trim()
      .toLowerCase();
  });
  if (headers.length === 0) throw new Error("Header row is empty.");

  const rows: Array<Record<string, string>> = [];
  for (let rowIdx = 2; rowIdx <= ws.rowCount; rowIdx++) {
    const excelRow = ws.getRow(rowIdx);
    const record: Record<string, string> = {};
    let anyValue = false;
    for (let colIdx = 0; colIdx < headers.length; colIdx++) {
      const header = headers[colIdx];
      if (!header) continue;
      const cell = excelRow.getCell(colIdx + 1);
      const raw = cell.value;
      let str: string;
      if (raw == null) {
        str = "";
      } else if (typeof raw === "object" && "richText" in raw) {
        str = raw.richText.map((t) => t.text).join("");
      } else if (typeof raw === "object" && "result" in raw) {
        // Formula cell — use computed result.
        str = String(raw.result ?? "");
      } else if (raw instanceof Date) {
        // Excel date → ISO YYYY-MM-DD (matches CSV shape).
        str = raw.toISOString().slice(0, 10);
      } else {
        str = String(raw);
      }
      str = str.trim();
      record[header] = str;
      if (str) anyValue = true;
    }
    if (anyValue) rows.push(record);
  }
  return rows;
}

/** True when the filename looks like a workbook rather than a CSV. */
export function isWorkbookFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return lower.endsWith(".xlsx") || lower.endsWith(".xlsm");
}
